import type { CaseDocument } from '@core/domain/model';
import { toListItem, typeLabel, typeOrder } from './formatter';
import type { DocumentsFilter, DocumentsListEntry, DocumentsSort } from './types';

/**
 * Порядок и разделы библиотеки — порт `order` и `entries` из `DocumentsViewModel`.
 *
 * Чистые функции без базы и без React: те же случаи, что и в Android-тестах,
 * проверяются здесь Vitest'ом.
 */

/**
 * Избранное идёт первым при **любой** сортировке, включая свою.
 *
 * Звезда — это закрепление наверх, а не просто пометка («Паспорт» §11,
 * [D-20](docs/DECISIONS.md)). Свой порядок действует внутри групп: сначала избранные
 * в заданном порядке, следом остальные. Поэтому перетащить строку выше избранных
 * нельзя — и это ожидаемо.
 */
const favoritesFirst = (documents: CaseDocument[]): CaseDocument[] =>
  [...documents].sort((a, b) => Number(b.isFavorite) - Number(a.isFavorite));

export function matches(document: CaseDocument, filter: DocumentsFilter): boolean {
  switch (filter.kind) {
    case 'all':
      return true;
    case 'favorites':
      return document.isFavorite;
    case 'type':
      return document.type === filter.type;
  }
}

/**
 * Полный порядок документов дела.
 *
 * `dragOrder` — порядок, пока строку тащат: база узнаёт о нём только когда палец
 * отпустят. В режиме перестановки сортировка всегда своя: переставлять строки внутри
 * группировки по типу нечего — она собирается сама.
 */
export function order(
  documents: CaseDocument[],
  sort: DocumentsSort,
  isReordering: boolean,
  dragOrder: string[] | null = null,
): CaseDocument[] {
  // База отдаёт список уже избранными вперёд, а нам нужен сам заданный порядок:
  // группировку по избранному накладываем поверх него.
  const dragged = dragOrder
    ?.map((id) => documents.find((document) => document.id === id))
    .filter((document): document is CaseDocument => Boolean(document));

  const manual =
    dragged && dragged.length === documents.length
      ? dragged
      : [...documents].sort((a, b) => a.sortOrder - b.sortOrder);

  if (isReordering) return favoritesFirst(manual);

  switch (sort) {
    case 'manual':
      return favoritesFirst(manual);
    case 'newest':
      return favoritesFirst([...documents].sort((a, b) => b.addedAt - a.addedAt));
    case 'byType':
      // Внутри типа сохраняем порядок, заданный юристом: он и есть оглавление.
      return favoritesFirst(manual).sort((a, b) => typeOrder(a.type) - typeOrder(b.type));
  }
}

/**
 * Строки списка: заголовки разделов появляются только при сортировке по типу.
 *
 * Это и есть замена папкам — разделы возникают сами, раскладывать документы руками
 * не нужно. Тянуть можно только в режиме перестановки: на Android ручка появлялась
 * молча при «Своём порядке», и о ней никто не знал (обратная связь № 1, пункт 8).
 */
export function entries(
  documents: CaseDocument[],
  sort: DocumentsSort,
  isReordering: boolean,
): DocumentsListEntry[] {
  if (isReordering || sort !== 'byType') {
    return documents.map((document) => ({
      kind: 'item',
      document: toListItem(document),
      canDrag: isReordering,
    }));
  }

  const byType = new Map<CaseDocument['type'], CaseDocument[]>();
  for (const document of documents) {
    byType.set(document.type, [...(byType.get(document.type) ?? []), document]);
  }

  return [...byType].flatMap(([type, ofType]): DocumentsListEntry[] => [
    { kind: 'header', title: typeLabel(type), count: ofType.length },
    ...ofType.map(
      (document): DocumentsListEntry => ({
        kind: 'item',
        document: toListItem(document),
        canDrag: false,
      }),
    ),
  ]);
}
